/**
 * Invoicing period helpers (P1: 1–15, P2: 16–last day of month).
 * Uses local calendar dates to avoid UTC off-by-one on YYYY-MM-DD strings.
 */

const PERIOD_ID_RE = /^(\d{4})-(\d{2})-P([12])$/;

/**
 * @param {string|null|undefined} dateStr - YYYY-MM-DD or ISO datetime prefix
 * @returns {Date|null}
 */
export function parseLocalDate(dateStr) {
  if (!dateStr) return null;
  const raw = String(dateStr).trim();
  const datePart = raw.includes('T') ? raw.split('T')[0] : raw.slice(0, 10);
  const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);
  return new Date(year, month - 1, day);
}

/**
 * @param {string|null|undefined} dateStr
 * @param {Intl.DateTimeFormatOptions} [options]
 * @returns {string}
 */
export function formatDateLocal(dateStr, options = {}) {
  const date = parseLocalDate(dateStr);
  if (!date) return 'N/A';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  });
}

/**
 * @param {string} periodId - e.g. 2026-06-P2
 * @returns {{ year: number, month: number, half: 1|2 }|null}
 */
export function parsePeriodId(periodId) {
  if (!periodId) return null;
  const m = String(periodId).match(PERIOD_ID_RE);
  if (!m) return null;
  return {
    year: parseInt(m[1], 10),
    month: parseInt(m[2], 10),
    half: parseInt(m[3], 10)
  };
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function formatYmd(year, month, day) {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

/**
 * Canonical inclusive bounds for an invoicing period.
 * @param {string} periodId
 * @returns {{ start: string, end: string }|null}
 */
export function getPeriodBounds(periodId) {
  const parsed = parsePeriodId(periodId);
  if (!parsed) return null;
  const { year, month, half } = parsed;
  if (half === 1) {
    return {
      start: formatYmd(year, month, 1),
      end: formatYmd(year, month, 15)
    };
  }
  const lastDay = new Date(year, month, 0).getDate();
  return {
    start: formatYmd(year, month, 16),
    end: formatYmd(year, month, lastDay)
  };
}

/**
 * Local start/end Date objects for filtering (end inclusive through end of day).
 * @param {string} periodId
 * @returns {{ startDate: Date, endDate: Date }|null}
 */
export function getPeriodBoundsAsDates(periodId) {
  const bounds = getPeriodBounds(periodId);
  if (!bounds) return null;
  const startDate = parseLocalDate(bounds.start);
  const endDate = parseLocalDate(bounds.end);
  if (!startDate || !endDate) return null;
  endDate.setHours(23, 59, 59, 999);
  return { startDate, endDate };
}

/**
 * @param {string|null|undefined} apiStart
 * @param {string|null|undefined} apiEnd
 * @param {string} periodId
 * @returns {boolean}
 */
export function datesMatchPeriodBounds(apiStart, apiEnd, periodId) {
  const canonical = getPeriodBounds(periodId);
  if (!canonical) return false;
  const norm = (s) => {
    const d = parseLocalDate(s);
    if (!d) return null;
    return formatYmd(d.getFullYear(), d.getMonth() + 1, d.getDate());
  };
  return norm(apiStart) === canonical.start && norm(apiEnd) === canonical.end;
}

/**
 * Display range for invoice UI: prefer canonical bounds from period_id.
 * @param {string} periodId
 * @param {string|null|undefined} apiStart
 * @param {string|null|undefined} apiEnd
 * @returns {{ start: string, end: string, usedCanonical: boolean }}
 */
export function getDisplayPeriodRange(periodId, apiStart, apiEnd) {
  const canonical = getPeriodBounds(periodId);
  if (!canonical) {
    return {
      start: apiStart || '',
      end: apiEnd || '',
      usedCanonical: false
    };
  }
  const matches = datesMatchPeriodBounds(apiStart, apiEnd, periodId);
  if (!matches && apiStart && apiEnd && typeof console !== 'undefined') {
    console.warn(
      `[invoicingPeriods] API dates for ${periodId} (${apiStart}–${apiEnd}) differ from canonical (${canonical.start}–${canonical.end}); displaying canonical.`
    );
  }
  return {
    start: canonical.start,
    end: canonical.end,
    usedCanonical: true
  };
}

/**
 * Build select options for recent invoicing periods.
 * @param {number} monthsBack - how many calendar months to include (from current month)
 * @returns {{ value: string, label: string }[]}
 */
export function buildPeriodSelectOptions(monthsBack = 12) {
  const options = [];
  const now = new Date();
  for (let i = 0; i < monthsBack; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const ym = `${year}-${pad2(month)}`;
    for (const half of [1, 2]) {
      const periodId = `${ym}-P${half}`;
      const bounds = getPeriodBounds(periodId);
      if (!bounds) continue;
      const startFmt = formatDateLocal(bounds.start, { year: undefined });
      const endFmt = formatDateLocal(bounds.end, { year: undefined });
      options.push({
        value: periodId,
        label: `${ym} Period ${half} (${startFmt} – ${endFmt})`
      });
    }
  }
  return options;
}

/**
 * @param {object} item
 * @returns {string|null|undefined}
 */
export function getRowActivityDate(item) {
  if (!item) return null;
  return item.activity_date || item.report_date;
}
